import {useCallback, useRef} from 'react'
import {SWRConfig, SWRConfiguration} from 'swr'

import env from 'lib/config/public-env'
import useToast from 'lib/hooks/use-toast'
import LogRocket from 'lib/logrocket'
import {ResponseError, swrFetcher as fetcher} from 'lib/utils/safe-fetch'
import {logoutOnce} from 'lib/user'

const errorTitle = 'Error reading data'
const errorDescription =
  'Try reloading this page or contact support if the problem persists. '

// Cypress runs tests quickly and runs into automatic deduping and throttling of requests.
// default is 2_000 ms
const dedupingInterval = env.CI === true ? 0 : 2_000

// Find url from key
const getUrlFromKey = (key: unknown | string | string[]): string =>
  typeof key === 'string'
    ? key
    : Array.isArray(key)
    ? getUrlFromKey(key[0])
    : 'unknown'

// SWRConfig wrapper
export default function SWRWrapper({children}: {children: React.ReactNode}) {
  const toast = useToast({status: 'error'})
  const logOutToastRef = useRef<string | number>()
  const onError = useCallback(
    (response: ResponseError | Error, key: any) => {
      const url = getUrlFromKey(key)
      const error: Error = response instanceof Error ? response : response.error

      // A 401 response indicates they are not authorized. Log them out.
      if (!(response instanceof Error) && response.status === 401) {
        if (logOutToastRef.current == null) {
          logOutToastRef.current = toast({
            title: '401 Unauthorized status detected',
            description: 'Your session may have expired. Logging out.'
          })
          logoutOnce(response.error, window.__user)
        }
      } else {
        LogRocket.captureException(error, {
          extras: {
            fetchUrl: url
          },
          tags: {
            type: 'fetch:swr'
          }
        })

        // Only show a single toast per endpoint
        const errorMessage = error.message.endsWith('.')
          ? error.message
          : error.message + '.'
        const description = `${errorMessage} ${errorDescription}`
        if (toast.isActive(url)) {
          toast.update(url, {description})
        } else {
          toast({
            description,
            id: url,
            title: errorTitle
          })
        }
      }
    },
    [toast]
  )

  const config: SWRConfiguration = {
    dedupingInterval,
    fetcher,
    onError,
    shouldRetryOnError: false
  }

  return <SWRConfig value={config}>{children}</SWRConfig>
}
